import { API_ENDPOINTS } from '../config/api'

export type LinkedEvidenceRow = {
  linkId: string
  questionAnswerId: string
  referenceType?: string
  source?: 'EVIDENCE_HUB' | 'POLICY_LIBRARY'
  evidenceId?: string
  policyId?: string
  policyVersionId?: string
  documentUuid?: string
  displayId?: string
  title?: string
  version?: string
  status?: string
  verificationStatus?: string
  freshnessStatus?: string
  controlMappings?: Array<{ controlKey: string; standardCode: string }>
  linkageSource?: string
  linkedAt?: string
}

export type QuestionEvidenceState = {
  enabled: boolean
  questionAnswerId?: string
  linkedEvidence?: LinkedEvidenceRow[]
  suggestedEvidence?: Array<{
    id: string
    displayId?: string
    title?: string
    verificationStatus?: string
    freshnessStatus?: string
    mappedControls?: string[]
    mappedStandards?: string[]
    crossStandardReuse?: boolean
    suggestionReason?: string
  }>
  reuseCandidates?: Array<{
    candidateId: string
    evidenceId: string
    reusableFrom: string
    targetStandard: string
    status: string
    reason?: string
  }>
  sufficiency?: {
    state: 'NONE' | 'PARTIAL' | 'SUFFICIENT' | 'STALE' | 'CONFLICTED'
    reason?: string
    verifiedCount?: number
    staleCount?: number
  }
  lineage?: Record<string, unknown>
}

function base(sessionId: string, questionKey: string) {
  return API_ENDPOINTS.COMPLIANCE_QUESTION_EVIDENCE_STATE(sessionId, questionKey)
}

export async function fetchQuestionEvidenceState(
  token: string,
  params: {
    sessionId: string
    questionKey: string
    domainKey: string
    canonicalControlCode?: string
    standardCode?: string
    answerValue?: string
  },
): Promise<QuestionEvidenceState> {
  const q = new URLSearchParams()
  q.set('domainKey', params.domainKey)
  if (params.canonicalControlCode) q.set('canonicalControlCode', params.canonicalControlCode)
  if (params.standardCode) q.set('standardCode', params.standardCode)
  if (params.answerValue) q.set('answerValue', params.answerValue)
  const res = await fetch(`${base(params.sessionId, params.questionKey)}?${q}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json?.message || `Failed to load evidence state (${res.status})`)
  return json?.data || { enabled: false }
}

export async function linkQuestionEvidence(
  token: string,
  params: {
    sessionId: string
    questionKey: string
    domainKey: string
    evidenceIds: string[]
    linkageSource?: string
  },
): Promise<LinkedEvidenceRow[]> {
  const res = await fetch(API_ENDPOINTS.COMPLIANCE_QUESTION_EVIDENCE_LINK(params.sessionId, params.questionKey), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      domainKey: params.domainKey,
      evidenceIds: params.evidenceIds,
      linkageSource: params.linkageSource || 'MANUAL',
    }),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json?.message || `Failed to link evidence (${res.status})`)
  return json?.data?.linked || []
}

export async function bulkLinkQuestionEvidence(
  token: string,
  params: {
    sessionId: string
    questionKey: string
    domainKey: string
    rawInput: string
    linkageSource?: string
  },
): Promise<{ linked: LinkedEvidenceRow[]; unresolved: Array<{ token: string; reason: string }> }> {
  const res = await fetch(API_ENDPOINTS.COMPLIANCE_QUESTION_EVIDENCE_LINK_BULK(params.sessionId, params.questionKey), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      domainKey: params.domainKey,
      rawInput: params.rawInput,
      linkageSource: params.linkageSource || 'MANUAL',
    }),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json?.message || `Failed to bulk link evidence (${res.status})`)
  return json?.data || { linked: [], unresolved: [] }
}

export async function linkQuestionPolicies(
  token: string,
  params: {
    sessionId: string
    questionKey: string
    domainKey: string
    policyRefs: Array<{ policyId: string; policyVersionId?: string | null; referenceType?: string }>
    linkageSource?: string
  },
): Promise<LinkedEvidenceRow[]> {
  const res = await fetch(
    API_ENDPOINTS.COMPLIANCE_QUESTION_EVIDENCE_LINK_POLICIES(params.sessionId, params.questionKey),
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        domainKey: params.domainKey,
        policyRefs: params.policyRefs,
        linkageSource: params.linkageSource || 'MANUAL',
      }),
    },
  )
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json?.message || `Failed to link policies (${res.status})`)
  return json?.data?.linked || []
}

export async function unlinkQuestionEvidence(
  token: string,
  params: { sessionId: string; questionKey: string; evidenceId: string },
): Promise<void> {
  const res = await fetch(
    API_ENDPOINTS.COMPLIANCE_QUESTION_EVIDENCE_UNLINK(params.sessionId, params.questionKey, params.evidenceId),
    { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } },
  )
  const json = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(json?.message || `Failed to unlink evidence (${res.status})`)
}
